import { io } from 'socket.io-client';

let API_URL;
if (process.env.REACT_APP_DEV==='dev') {
  API_URL = process.env.REACT_APP_API_URL;
} else {
  API_URL = process.env.REACT_APP_API_BACKEND_URL;
}

let socket = null;
let setupComplete = false;
let connectionFailed = false;
const eventHandlers = new Map();

/**
 * Socket.io bağlantısını başlatır
 * @param {string} token - Auth token
 * @returns {Object} - Socket.io bağlantısı
 */
export const initializeSocket = (token) => {
  if (!token) {
    // console.error('WebSocket bağlantısı kurulamadı: Token eksik');
    return null;
  }

  // If connection repeatedly failed, don't try to reconnect
  if (connectionFailed) {
    return null;
  }

  if (socket && setupComplete) {
    return socket;
  }

  // Önceki soketin bağlantısını kapat
  if (socket) {
    socket.disconnect();
  }

  // URL'den https:// veya http:// kısmını kaldır, sadece hostname:port kalsın
  let socketUrl = API_URL;
  try {
    const url = new URL(API_URL);
    socketUrl = url.host; // sadece hostname:port
    // console.log('WebSocket URL:', socketUrl);
  } catch (error) {
    // Fall back to polling only if there's an issue with the URL
    connectionFailed = true;
    return null;
  }

  try {
    // WebSocket bağlantısını kur
    socket = io(socketUrl, {
      auth: {
        token
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 2, // Reduce reconnection attempts
      reconnectionDelay: 2000,
      timeout: 5000, // Reduce timeout for faster fallback
      autoConnect: true
    });

    // Bağlantı olaylarını dinle
    socket.on('connect', () => {
      // console.log('WebSocket bağlantısı kuruldu');
      setupComplete = true;
      connectionFailed = false;
      
      // Kullanıcı kimliğini doğrula
      socket.emit('authenticate', { token });
      // console.log('WebSocket kimlik doğrulama isteği gönderildi');
    });

    let errorCount = 0;
    const MAX_ERRORS = 2;

    socket.on('connect_error', (err) => {
      // console.error('WebSocket bağlantı hatası:', err.message);
      setupComplete = false;
      errorCount++;
      
      // After multiple errors, mark as failed to prevent future connection attempts
      if (errorCount >= MAX_ERRORS) {
        connectionFailed = true;
        socket.disconnect();
      }
    });

    socket.on('disconnect', (reason) => {
      // console.log('WebSocket bağlantısı kesildi:', reason);
      setupComplete = false;
      
      // If server closed the connection, mark as failed
      if (reason === 'io server disconnect') {
        connectionFailed = true;
      }
    });

    return socket;
  } catch (error) {
    connectionFailed = true;
    return null;
  }
};

/** Socket event names for report lifecycle */
export const REPORT_SOCKET_EVENTS = [
  'report:created',
  'report:updated',
  'report:deleted',
  'report:statusChanged',
];

/**
 * Subscribe to all report lifecycle socket events
 */
export const subscribeToReportEvents = (handler) => {
  if (!socket || connectionFailed) return;

  REPORT_SOCKET_EVENTS.forEach((eventName) => {
    if (eventHandlers.has(eventName)) {
      socket.off(eventName, eventHandlers.get(eventName));
    }
    const wrapped = (payload) => handler(eventName, payload);
    socket.on(eventName, wrapped);
    eventHandlers.set(eventName, wrapped);
  });
};

/**
 * Unsubscribe from report lifecycle socket events
 */
export const unsubscribeFromReportEvents = () => {
  REPORT_SOCKET_EVENTS.forEach((eventName) => {
    unsubscribeFromEvent(eventName);
  });
};

/**
 * Whether the socket is currently connected
 */
export const isSocketConnected = () => Boolean(socket?.connected && setupComplete);

/**
 * Bildirim olaylarını dinlemeye başlar
 * @param {string} eventName - Dinlenecek olay adı
 * @param {Function} callback - Olay gerçekleştiğinde çağrılacak fonksiyon
 */
export const subscribeToEvent = (eventName, callback) => {
  if (!socket || connectionFailed) {
    // console.error('WebSocket bağlantısı başlatılmamış');
    return;
  }

  // Aynı olay için önceki dinleyicileri kaldır
  if (eventHandlers.has(eventName)) {
    socket.off(eventName, eventHandlers.get(eventName));
  }

  // Yeni dinleyici ekle
  socket.on(eventName, callback);
  eventHandlers.set(eventName, callback);
  // console.log(`${eventName} olayı için dinleyici eklendi`);
};

/**
 * Bildirim olayını dinlemeyi durdurur
 * @param {string} eventName - Dinlemeyi durduracak olay adı
 */
export const unsubscribeFromEvent = (eventName) => {
  if (!socket) {
    return;
  }

  if (eventHandlers.has(eventName)) {
    socket.off(eventName, eventHandlers.get(eventName));
    eventHandlers.delete(eventName);
    // console.log(`${eventName} olayı için dinleyici kaldırıldı`);
  }
};

/**
 * WebSocket bağlantısını kapatır
 */
export const closeSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    setupComplete = false;
    // Don't reset connectionFailed here to prevent reconnecting
    eventHandlers.clear();
    // console.log('WebSocket bağlantısı kapatıldı');
  }
};

/**
 * WebSocket bağlantı durumunu sıfırlar (test/dev için)
 */
export const resetConnectionState = () => {
  connectionFailed = false;
}; 